import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Globe, MapPin, Heart, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";

const Missions = () => {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-24 pb-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 animate-fade-in">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-6">
              <Globe className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-6">
              {t("missions.title")}
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t("missions.subtitle")}
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-12">
            <section className="bg-card rounded-lg p-8 shadow-lg">
              <h2 className="text-3xl font-bold text-foreground mb-6">{t("missions.heart.title")}</h2>
              <p className="text-muted-foreground mb-4">
                {t("missions.heart.text1")}
              </p>
              <p className="text-muted-foreground">
                {t("missions.heart.text2")}
              </p>
            </section>

            <section className="bg-card rounded-lg p-8 shadow-lg">
              <h2 className="text-3xl font-bold text-foreground mb-6">{t("missions.fields.title")}</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Link
                  to="/guatemala-mission"
                  className="border-2 border-border rounded-lg p-6 hover:border-primary hover:shadow-lg transition-all group"
                >
                  <div className="flex items-start gap-4">
                    <MapPin className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-2xl mb-2 group-hover:text-primary transition-colors">
                        {t("missions.guatemala.title")}
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        {t("missions.guatemala.desc")}
                      </p>
                      <span className="text-primary font-semibold">{t("missions.guatemala.cta")}</span>
                    </div>
                  </div>
                </Link>

                <Link
                  to="/mexico-mission"
                  className="border-2 border-border rounded-lg p-6 hover:border-primary hover:shadow-lg transition-all group"
                >
                  <div className="flex items-start gap-4">
                    <MapPin className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-2xl mb-2 group-hover:text-primary transition-colors">
                        {t("missions.mexico.title")}
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        {t("missions.mexico.desc")}
                      </p>
                      <span className="text-primary font-semibold">{t("missions.mexico.cta")}</span>
                    </div>
                  </div>
                </Link>

              </div>
            </section>

            <section className="bg-card rounded-lg p-8 shadow-lg">
              <h2 className="text-3xl font-bold text-foreground mb-6">{t("missions.serve.title")}</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start gap-4">
                  <Heart className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-lg mb-2">{t("missions.serve.compassion")}</h3>
                    <p className="text-muted-foreground">{t("missions.serve.compassion.desc")}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Users className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-lg mb-2">{t("missions.serve.community")}</h3>
                    <p className="text-muted-foreground">{t("missions.serve.community.desc")}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Globe className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-lg mb-2">{t("missions.serve.partnerships")}</h3>
                    <p className="text-muted-foreground">{t("missions.serve.partnerships.desc")}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Heart className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-lg mb-2">{t("missions.serve.evangelism")}</h3>
                    <p className="text-muted-foreground">{t("missions.serve.evangelism.desc")}</p>
                  </div>
                </div>
              </div>
            </section>

            <section className="bg-card rounded-lg p-8 shadow-lg">
              <h2 className="text-3xl font-bold text-foreground mb-6">{t("missions.involved.title")}</h2>
              <ul className="space-y-3 text-muted-foreground mb-6">
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">•</span>
                  <span><strong>{t("missions.involved.trip")}</strong> {t("missions.involved.trip.desc")}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">•</span>
                  <span><strong>{t("missions.involved.pray")}</strong> {t("missions.involved.pray.desc")}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">•</span>
                  <span><strong>{t("missions.involved.give")}</strong> {t("missions.involved.give.desc")}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">•</span>
                  <span><strong>{t("missions.involved.sponsor")}</strong> {t("missions.involved.sponsor.desc")}</span>
                </li>
              </ul>
              <p className="text-muted-foreground mb-6">
                {t("missions.involved.text")}
              </p>
              <Button size="lg" onClick={() => window.location.href = "/#contact"}>
                {t("missions.involved.cta")}
              </Button>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Missions;
